import { useState, useEffect, useContext, useCallback } from 'react';
import { UserContext } from '@/components';
import { useNotification } from '@/components/general/NotificationProvider';
import { shareFolder, listFolderShareUsers, lookupUser, ShareUser } from '@/api';
import XLight from '@/assets/xLight.svg';
import TrashLight from '@/assets/trash-light.svg';
import Modal from '../../general/Modal';

interface EditSharingProps {
  show: boolean;
  folderId: number;
  onClose: () => void;
  onRefresh: () => void;
}

export function EditSharing({ show, folderId, onClose, onRefresh }: EditSharingProps) {
  const user = useContext(UserContext);
  const { showNotification } = useNotification();
  const [shareUsers, setShareUsers] = useState<ShareUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<ShareUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ShareUser | null>(null);

  const loadCurrentFolderSharing = useCallback(async () => {
    if (!user?.userId) return;

    try {
      const result = await listFolderShareUsers(user.userId, folderId);
      if (result.succeeded) {
        setShareUsers(result.foundUsers || []);
      } else {
        showNotification('Error listing folder share users', 'error');
      }
    } catch {
      showNotification('Error loading sharing information', 'error');
    }
  }, [user?.userId, folderId, showNotification]);

  useEffect(() => {
    if (show && folderId) {
      loadCurrentFolderSharing();
    }
  }, [show, folderId, loadCurrentFolderSharing]);

  const queryUsers = useCallback(
    async (email: string) => {
      setLoading(true);
      try {
        const result = await lookupUser(email);
        if (result.succeeded) {
          setSearchResults(result.foundUsers || []);
        } else {
          showNotification('User lookup server call failed', 'error');
        }
      } catch {
        showNotification('Unexpected error occurred', 'error');
      } finally {
        setLoading(false);
      }
    },
    [showNotification],
  );

  useEffect(() => {
    if (searchTerm && searchTerm.length > 2 && (!selectedUser || searchTerm !== selectedUser.email)) {
      const timeoutId = setTimeout(() => {
        queryUsers(searchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, selectedUser, queryUsers]);

  const handleUserSelect = (user: ShareUser) => {
    const userExists = shareUsers.some((u) => u.guid === user.guid);
    if (userExists) {
      showNotification('User is already in the list', 'warning');
      return;
    }

    setShareUsers([...shareUsers, user]);
    setSearchTerm('');
    setSelectedUser(null);
    setSearchResults([]);
  };

  const handleRemoveUser = (userToRemove: ShareUser) => {
    setShareUsers(shareUsers.filter((u) => u.guid !== userToRemove.guid));
  };

  const handleShare = async () => {
    if (!user?.userId) return;

    try {
      const userIds = shareUsers.map((u) => u.guid);
      const result = await shareFolder(user.userId, folderId, userIds);

      if (result.succeeded) {
        onRefresh();
        handleClose();
      } else {
        showNotification('Server call to update folder sharing failed!', 'error');
      }
    } catch {
      showNotification('Failed to share folder', 'error');
    }
  };

  const handleClose = () => {
    setShareUsers([]);
    setSearchTerm('');
    setSearchResults([]);
    setSelectedUser(null);
    onClose();
  };

  if (!show) return null;

  return (
    <Modal open={show} onClose={handleClose} contanerClassName="w-[700px] max-w-[90vw] bg-white shadow-xl rounded-lg">
      <div className="flex cursor-pointer items-center justify-between p-6">
        <h3 className="text-xl font-normal text-gray-900">Share folder with users</h3>
        <button onClick={handleClose} className="cursor-pointer p-1">
          <XLight className="h-6 w-6" />
        </button>
      </div>

      <div className="px-6 pb-6">
        {/* Current shared users */}
        {shareUsers.length > 0 && (
          <div
            className="d-flex mb-6 w-fit"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              rowGap: '.25em',
              columnGap: '1em',
            }}
          >
            {shareUsers.map((shareUser) => (
              <div
                key={shareUser.guid}
                className="mb-3 flex items-center justify-between rounded-full border border-gray-300 bg-gray-50 px-4 py-2"
              >
                <span className="flex-1 text-sm text-gray-900">{shareUser.email}</span>
                <button onClick={() => handleRemoveUser(shareUser)} className="ml-2 p-1">
                  <TrashLight className="h-4 w-4 cursor-pointer text-gray-400 hover:text-gray-600" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Search Input */}
        <div className="relative">
          <input
            type="email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by email"
            className="w-full border-b border-gray-300 bg-transparent px-0 py-3 text-base placeholder-gray-400 focus:border-gray-500 focus:outline-none"
          />
          {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedUser(null);
                setSearchResults([]);
              }}
              className="absolute top-1/2 right-8 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600"
              style={{ right: '58px' }}
            >
              ✕
            </button>
          )}
          <div
            className={`absolute top-1/2 right-0 -translate-y-1/2 text-black transition-transform duration-200 ${searchResults.length > 0 ? 'rotate-180' : ''}`}
          >
            ▼
          </div>
          {loading && (
            <div className="absolute top-1/2 right-6 -translate-y-1/2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent"></div>
            </div>
          )}
          {searchResults.length > 0 && (
            <div className="absolute top-full right-0 left-0 z-50 mt-1 max-h-60 overflow-y-auto border border-gray-200 bg-white shadow-lg">
              {searchResults.map((result) => (
                <div
                  key={result.guid}
                  onClick={() => handleUserSelect(result)}
                  className="cursor-pointer border-b border-gray-100 px-0 py-3 last:border-b-0 hover:bg-gray-100"
                >
                  <div className="ml-1 text-gray-900">{result.email}</div>
                  {result.name && <div className="text-sm text-gray-600">{result.name}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-4 px-6 py-4">
        <button
          onClick={handleShare}
          className="flex cursor-pointer items-center gap-2 rounded-full bg-gray-100 px-6 py-2 text-black hover:bg-gray-200"
        >
          <span>Share</span>
          <div className="mx-2 h-5 w-px bg-gray-400"></div>
          <svg className="h-5 w-5 rotate-310" viewBox="0 0 24 24">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
        <button
          onClick={handleClose}
          className="cursor-pointer rounded-full bg-gray-100 px-6 py-2 text-gray-800 hover:bg-gray-200"
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
}
